import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["value", "disableFuture", "disablePast", "onPickChange", "hasInput", "locale", "inputProps", "format", "disabled", "id", "minDate", "maxDate", "defaultCalendarMonth"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* © 2023 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { EmbersInnerThemeProvider } from '../rtlProvider';
import moment from 'moment';
import { LocalizationProvider, CalendarPicker, PickersDay } from './lab/src';
import { CustomDateAdapter, ArrowIcon } from './base';
import { EmbersDateField } from './dateField';
import { getFormat, focusToPointedElement, convertToMoment, dateAdapter } from '../utils/dateUtils';
import { StyledDateCalendar, datePickerTheme } from './dateStyles';
import { useId } from '../utils/useId';
function EmbersDateCalendar(props) {
  var value = props.value,
    _props$disableFuture = props.disableFuture,
    disableFuture = _props$disableFuture === void 0 ? true : _props$disableFuture,
    _props$disablePast = props.disablePast,
    disablePast = _props$disablePast === void 0 ? false : _props$disablePast,
    onPickChange = props.onPickChange,
    hasInput = props.hasInput,
    localeProp = props.locale,
    _props$inputProps = props.inputProps,
    inputProps = _props$inputProps === void 0 ? {} : _props$inputProps,
    format = props.format,
    disabled = props.disabled,
    _id = props.id,
    minDate = props.minDate,
    maxDate = props.maxDate,
    defaultCalendarMonth = props.defaultCalendarMonth,
    otherProps = _objectWithoutProperties(props, _excluded);
  var intl = useIntl();
  var id = useId(_id);
  var locale = localeProp || moment.locale();
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    date = _useState2[0],
    setDate = _useState2[1];
  var calendarRef = useRef(null);
  var dateFormat = getFormat(format, locale);
  useEffect(function () {
    if (value) {
      setDate(convertToMoment(value));
    }
  }, [value]);
  var handleInputChange = function handleInputChange(date, error) {
    setDate(date);
    if (date && !error) {
      onPickChange === null || onPickChange === void 0 || onPickChange(date.toDate());
    }
  };
  var handlePickerChange = function handlePickerChange(value, selectionState, event) {
    setDate(value);
    if (value) {
      onPickChange === null || onPickChange === void 0 || onPickChange(value.toDate(), event === null || event === void 0 ? void 0 : event.type);
    }
  };
  var renderPickerDay = function renderPickerDay(day, selectedDates, pickersDayProps) {
    return /*#__PURE__*/React.createElement(PickersDay, _extends({}, pickersDayProps, {
      disableRipple: true
    }));
  };
  var convertDate = useMemo(function () {
    return dateAdapter(date, disableFuture, disablePast);
  }, [date, disableFuture, disablePast]);
  return /*#__PURE__*/React.createElement(EmbersInnerThemeProvider, {
    theme: datePickerTheme
  }, /*#__PURE__*/React.createElement(LocalizationProvider, {
    dateAdapter: CustomDateAdapter,
    locale: locale
  }, /*#__PURE__*/React.createElement(StyledDateCalendar, {
    id: id,
    className: "EmbersDateCalendar-root",
    onKeyDown: hasInput ? focusToPointedElement("#".concat(id, " .EmbersDateCalendar-view"), "#".concat(id, " .EmbersDateCalendar-input input"), 'MuiPickersDay-root') : undefined
  }, !!hasInput && /*#__PURE__*/React.createElement(EmbersDateField, _extends({
    className: "EmbersDateCalendar-input",
    label: intl.formatMessage({
      id: 'embers.date.label',
      defaultMessage: 'Date'
    }),
    value: date,
    disabled: disabled,
    handlePickerChange: handleInputChange,
    disableFuture: disableFuture,
    disablePast: disablePast,
    format: dateFormat,
    locale: locale
  }, inputProps, {
    inputProps: _objectSpread({
      id: "".concat(id, "-input")
    }, inputProps.inputProps)
  })), /*#__PURE__*/React.createElement(CalendarPicker, _extends({
    className: "EmbersDateCalendar-view",
    ref: calendarRef,
    date: convertDate,
    components: {
      LeftArrowIcon: ArrowIcon('left'),
      RightArrowIcon: ArrowIcon('right')
    },
    componentsProps: {
      leftArrowButton: {
        disableRipple: true,
        className: 'EmbersCalendarPicker-switchLeft'
      },
      rightArrowButton: {
        disableRipple: true,
        className: 'EmbersCalendarPicker-switchRight'
      }
    },
    views: ['day'],
    renderDay: renderPickerDay,
    disableHighlightToday: false,
    disableFuture: disableFuture,
    disablePast: disablePast,
    onChange: handlePickerChange,
    autoFocus: false,
    disabled: disabled,
    minDate: minDate ? convertToMoment(minDate) : undefined,
    maxDate: maxDate ? convertToMoment(maxDate) : undefined,
    defaultCalendarMonth: defaultCalendarMonth ? convertToMoment(maxDate) : undefined
  }, otherProps)))));
}
export { EmbersDateCalendar };